import { useEffect, useMemo, useRef } from 'react';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { typography, radius } from '@/shared/theme/tokens';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { useLiveSimulator } from '../../hooks/useLiveSimulator';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import {
  ACTIONABLE_REC_STATUSES, computeHeadline, rankRecs, rankAlerts,
  isFeedDegraded, freshnessAgeSeconds, FRESHNESS_STALE_SECONDS,
} from './consensusMeta';
import { StatusBar } from './StatusBar';
import { FurnaceHero } from './FurnaceHero';
import { AlertRail } from './AlertRail';
import { RecommendationTicker } from './RecommendationTicker';
import { PerformanceStats } from './PerformanceStats';

// Keyframes for the presentation layer. Injected once; gated per-element by the
// `animate` flag (reduced-motion aware).
const KEYFRAMES = `
@keyframes lm-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.35); opacity: 0.55; } }
@keyframes lm-fade-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes lm-flash-once { 0% { box-shadow: 0 0 0 0 currentColor; } 60% { box-shadow: 0 0 0 4px transparent; } 100% { box-shadow: none; } }
`;

/**
 * The operator control-room screen. An AI advisory ambient display — its job is
 * "does the AI need my attention, and what changed since I last looked?", NOT
 * to operate the furnace. The adaptive headline owns the top; actions own the
 * dominant column; the furnace is a supporting identity/spatial anchor.
 */
export function ConsensusScreen({ deploymentName, svgUrl, seedAlerts, seedRecs, isLoading, isError, onSelectRec, onSelectAlert }) {
  const c = useThemeColors();
  const { isDesktop } = useBreakpoint();
  const reduced = useReducedMotion();
  const animate = !reduced;

  const { alerts, recs, clock, flashId, jitterActive, dataAsOf } = useLiveSimulator(seedAlerts, seedRecs);

  // Never reason on inputs we don't trust: too many stale → pause the advisory.
  const degraded = useMemo(() => isFeedDegraded(alerts), [alerts]);

  const { criticalCount, watchingCount } = useMemo(() => ({
    criticalCount: alerts.filter((a) => a.status === 'critical').length,
    watchingCount: alerts.filter((a) => a.status === 'warning').length,
  }), [alerts]);

  // Recs that still need an operator decision — suppressed entirely when degraded.
  const actionableRecs = useMemo(
    () => (degraded ? [] : recs.filter((r) => ACTIONABLE_REC_STATUSES.includes(r.status))),
    [recs, degraded],
  );

  // A rec is "critical-linked" when a currently-critical alert points at it.
  const criticalRecIds = useMemo(
    () => new Set(alerts.filter((a) => a.status === 'critical' && a.recId).map((a) => a.recId)),
    [alerts],
  );
  const rankedRecs = useMemo(() => rankRecs(actionableRecs, criticalRecIds), [actionableRecs, criticalRecIds]);
  const rankedAlerts = useMemo(() => rankAlerts(alerts), [alerts]);

  const headline = useMemo(
    () => computeHeadline({ degraded, criticalCount, actionsCount: actionableRecs.length, watchingCount }),
    [degraded, criticalCount, actionableRecs.length, watchingCount],
  );

  // Remember the last trusted state so DEGRADED can show "last valid: …" without
  // ever blending two primary states in the headline.
  const lastValidRef = useRef(null);
  useEffect(() => {
    if (headline.state !== 'degraded') lastValidRef.current = { label: headline.label, at: clock };
    // clock intentionally omitted — capture the time at the moment state changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headline.state, headline.count]);

  const headlineOut = useMemo(() => {
    if (headline.state === 'degraded' && lastValidRef.current) {
      const t = lastValidRef.current.at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      return { ...headline, subtext: `Last valid: ${lastValidRef.current.label} at ${t}` };
    }
    return headline;
  }, [headline]);

  const freshness = useMemo(() => {
    const ageSeconds = freshnessAgeSeconds(dataAsOf, clock);
    return { ageSeconds, stale: degraded || ageSeconds > FRESHNESS_STALE_SECONDS };
  }, [dataAsOf, clock, degraded]);

  if (isError) {
    return <ErrorState message="Couldn't load live data for this deployment." />;
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
      <style>{KEYFRAMES}</style>

      <StatusBar
        deploymentName={deploymentName}
        clock={clock}
        jitterActive={jitterActive}
        animate={animate}
        headline={headlineOut}
        freshness={freshness}
      />

      {isLoading && alerts.length === 0 && recs.length === 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '2fr 1fr' : '1fr', gap: 12, flex: 1, minHeight: 0 }}>
          <SkeletonCard rows={6} height="100%" />
          <SkeletonCard rows={6} height="100%" />
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? '2fr 1fr' : '1fr',
          gap: 12, flex: 1, minHeight: 0,
        }}>
          {/* Left, dominant — the action band: what the AI wants me to do, and what's drifting */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, minWidth: 0 }}>
            <div style={{ flex: '1 1 56%', minHeight: 220, display: 'flex' }}>
              <RecommendationTicker recs={rankedRecs} paused={degraded} animate={animate} onSelect={onSelectRec} />
            </div>
            <div style={{ flex: '1 1 44%', minHeight: 180, display: 'flex' }}>
              <AlertRail alerts={rankedAlerts} flashId={flashId} animate={animate} onSelect={onSelectAlert} />
            </div>
          </div>

          {/* Right, supporting — identity/spatial anchor + the shift handoff summary */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, minWidth: 0 }}>
            <FurnaceHero svgUrl={svgUrl} params={rankedAlerts} animate={animate} />
            <div style={{ flex: '0 1 auto', display: 'flex' }}>
              <PerformanceStats alerts={alerts} recs={recs} />
            </div>
          </div>
        </div>
      )}

      {/* Reserved info / branding strip (CEO's "other half" for info/ads later) */}
      <div style={{
        flexShrink: 0, padding: '6px 14px', borderRadius: radius.lg,
        border: `1px dashed ${c.border}`, background: c.bgSubtle,
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: typography.fontSize.xs, color: c.textSubtle,
        letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>campaign</span>
        Live operations channel · info strip reserved
      </div>
    </div>
  );
}
